import createAuth0Client, {
  Auth0Client,
  Auth0ClientOptions,
  AuthenticationError,
  GetIdTokenClaimsOptions,
  GetTokenSilentlyOptions,
  GetTokenWithPopupOptions,
  LogoutOptions,
  RedirectLoginOptions,
} from '@auth0/auth0-spa-js';
import ServerAPI from '@/services/serverAPI';
import { computed, reactive, watchEffect } from 'vue';
import { Auth0State, Auth0ComputedState } from './types';
import { intersection } from 'lodash';
import { RouteName, RoutePath } from '@/enums/Routes';
import { ErrorCodes } from '@/enums/ErrorCodes';
import { config } from '@/services/config';
import { mainRoles } from '@/enums/UserRole';
import { genericTranslations } from '@/plugins/localisation/index';
import { RouteLocationNormalized } from 'vue-router';

let client: Auth0Client;

const state: Auth0State = reactive({
  loading: true,
  isAuthenticated: false,
  user: {},
  userRoles: [],
  popupOpen: false,
  error: null,
  threwAuth0Error: false,
});

const loginWithRedirect = (appContext: RedirectLoginOptions) => {
  return client.loginWithRedirect(appContext);
};

const getIdTokenClaims = (options: GetIdTokenClaimsOptions) => {
  return client.getIdTokenClaims(options);
};

const getTokenSilently = (options: GetTokenSilentlyOptions) => {
  return client.getTokenSilently(options);
};

const getTokenWithPopup = (options: GetTokenWithPopupOptions) => {
  return client.getTokenWithPopup(options);
};

export const logout = async (options: LogoutOptions) => {
  return client.logout(options);
};

export const authState: Auth0ComputedState = {
  isAuthenticated: computed(() => state.isAuthenticated),
  loading: computed(() => state.loading),
  user: computed(() => state.user),
  userRoles: computed(() => state.userRoles),
  userIds: {
    marketId: null,
    employeeId: null,
    teamId: null,
  },
  bearerToken: null,
  methods: {
    getIdTokenClaims,
    getTokenSilently,
    getTokenWithPopup,
    loginWithRedirect,
    logout,
  },
};

const checkAccessRole = (to: RouteLocationNormalized, next: Function) => {
  const allowedRoles = to.meta.allowedRoles as Array<string>;

  if (!allowedRoles) return next();

  const { userRoles } = authState;

  if (intersection(allowedRoles, userRoles.value).length > 0) {
    return next();
  }

  next({
    name: RouteName.ERROR,
    params: {
      errorCode: ErrorCodes.NOT_AUTHORISED,
    },
  });
};

export const routeGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: Function
) => {
  const { loginWithRedirect } = authState.methods;
  const { isAuthenticated, loading } = authState;

  const verify = () => {
    if (isAuthenticated.value) {
      return checkAccessRole(to, next);
    }
    loginWithRedirect({ appState: { targetUrl: to.fullPath } });
  };

  if (!loading.value) {
    return verify();
  }

  watchEffect(() => {
    if (loading.value === false) {
      return verify();
    }
  });
};

export const setupAuth = async (callbackRedirect: Function) => {
  const currentRoute = window.location.protocol + '//' + window.location.host;

  const auth0Config: Auth0ClientOptions = {
    domain: config.integrations.auth0.domain,
    client_id: config.integrations.auth0.clientId,
    redirect_uri: window.location.protocol + '//' + window.location.host,
    audience: ServerAPI.serverUrl(),
  };

  client = await createAuth0Client(auth0Config);

  try {
    const callbackResult = await client.handleRedirectCallback();
    callbackRedirect(callbackResult ? callbackResult.appState : null);
  } catch (e) {
    state.error = e;

    // Logout and redirect if handleRedirectCallback throws an AuthenticationError
    if (state.error && state.error instanceof AuthenticationError) {
      state.threwAuth0Error = true;
      const errorDescription = encodeURIComponent(
        state.error.error_description
      );
      client.logout({
        returnTo: `${currentRoute}${RoutePath.ERROR}?error=${state.error.error}&errorDescription=${errorDescription}&errorSource=auth0`,
      });
    }
  } finally {
    // Avoid redirect loops if an AuthenticationError occurred
    if (!state.threwAuth0Error) {
      state.isAuthenticated = await client.isAuthenticated();

      const user = await client.getUser();
      state.user = user;

      if (user) {
        state.userRoles = user['https://pharos.com/roles'];
        authState.userIds.employeeId = user['https://pharos.com/employee_id'];
        authState.userIds.marketId = user['https://pharos.com/market_id'];
        authState.userIds.teamId = user['https://pharos.com/team_id'];
      }

      if (state.isAuthenticated) {
        const accessToken = await client.getTokenSilently();
        authState.bearerToken = accessToken;
      }

      const usersMainRoles = intersection(state.userRoles, mainRoles);

      const t = genericTranslations.global.t as (key: string) => string;
      if (user && usersMainRoles.length === 0) {
        const errorText = t(`error.${ErrorCodes.MISSING_MAIN_ROLE}`).replaceAll(
          ' ',
          '%20'
        );
        client.logout({
          returnTo: `${currentRoute}${RoutePath.ERROR}?error=Error&errorDescription=${errorText}`,
        });
      } else {
        state.loading = false;
      }
    }
  }

  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    install: (app: any) => {
      app.config.globalProperties.$auth = authState;
      app.provide('auth', authState);
    },
  };
};
